import { FC, useEffect } from 'react';
import { formatDateForCheckingState } from '../../../../utilities/dateFormatters';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import GridItem from '../../../layout/GridComponents/GridItem';
import FundCorrelationMatrix from './FundCorrelationMatrix.component';
import HistoricalPerformanceChart from './HistoricalPerformanceChart.component';
import PerformanceReportTables from './PerformanceReportTables.component';
import { Select } from '../../../../types/redux/pages/PagesStore';
import { useDispatch } from 'react-redux';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
} from '../../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import { Grid } from '@mui/material';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useFundPerformanceAnalysisPageQuery } from '../../../../api/rs-hg/risk/hooks/queries';
import type { FundPerformanceAnalysisPayload } from '../../../../api/rs-hg/risk/fundPerformanceAnalysis.types';

const PerformanceReport: FC<FundInfoComponentProps> = ({
  fundId,
  positionDate,
}) => {
  const dispatch = useDispatch();
  const lookBackPeriod = useTitleBarSelect('lookBackPeriod') || '1';
  const activeDate =
    positionDate ?? formatDateForCheckingState(new Date()) ?? '';

  useEffect(() => {
    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'Look Back Period:',
        titleBarKey: 'lookBackPeriod',
        values: [
          {
            label: 'One Year',
            value: '1',
          },
          {
            label: 'Three Year',
            value: '3',
          },
        ],
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: '1',
      },
    ];
    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent('lookBackPeriod', REMOVE_SELECT_FROM_TITLEBAR),
      );
    };
  }, [dispatch]);

  const performanceQuery = useFundPerformanceAnalysisPageQuery({
    date: activeDate,
    aperiod: lookBackPeriod,
    fundName: fundId,
  });

  return (
    <QueryBoundary<FundPerformanceAnalysisPayload>
      isLoading={performanceQuery.isLoading}
      isFetching={performanceQuery.isFetching}
      isError={performanceQuery.isError}
      error={performanceQuery.error}
      data={performanceQuery.data?.data}
      loadingMessages={['Loading Performance Report Data...']}
      errorMessage="No Data Available."
      noDataMessage="No performance report data available."
    >
      {(payload) => (
        <Grid container spacing={2} style={{ padding: 8 }}>
          <GridItem
            xs={7}
            style={{
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <HistoricalPerformanceChart payload={payload} />
            <FundCorrelationMatrix payload={payload} fundId={fundId} />
          </GridItem>
          <GridItem
            xs={5}
            style={{
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <PerformanceReportTables
              payload={payload}
              lookBackPeriod={lookBackPeriod}
            />
          </GridItem>
        </Grid>
      )}
    </QueryBoundary>
  );
};

export default PerformanceReport;
